import React, { useState, useEffect } from 'react'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Typography,
  Chip,
  LinearProgress
} from '@mui/material'
import {
  AccessTime,
  Warning,
  CreditCard,
  CheckCircle
} from '@mui/icons-material'
import BillingService, { TrialStatus } from '../services/BillingService'
import { formatCurrencySimple } from '../utils/currency'

interface TrialStatusBannerProps {
  onTrialStatusChange?: (status: TrialStatus | null) => void
}

export const TrialStatusBanner: React.FC<TrialStatusBannerProps> = ({
  onTrialStatusChange
}) => {
  const [trialStatus, setTrialStatus] = useState<TrialStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadTrialStatus()
  }, [])

  const loadTrialStatus = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await BillingService.getTrialStatus()

      setTrialStatus(data)
      onTrialStatusChange?.(data)
    } catch (err: any) {
      console.error('Error loading trial status:', err)
      setError('Unable to load trial status')
    } finally {
      setLoading(false)
    }
  }

  const handleUpgrade = () => {
    // Marketplace customers manage subscriptions in Azure Portal
    if (trialStatus?.purchaseSource === 'Marketplace') {
      window.location.href = 'https://portal.azure.com/#view/HubsExtension/BrowseResourceBlade/resourceType/Microsoft.SaaS%2Fresources'
    } else {
      window.location.href = '/billing/upgrade'
    }
  }

  const handleBillingPortal = async () => {
    // Marketplace customers manage billing in Azure Portal
    if (trialStatus?.purchaseSource === 'Marketplace') {
      window.location.href = 'https://portal.azure.com/#view/HubsExtension/BrowseResourceBlade/resourceType/Microsoft.SaaS%2Fresources'
      return
    }

    // Direct customers use Stripe billing portal
    try {
      const response = await BillingService.getBillingPortal({
        returnUrl: window.location.href
      })

      // ApiClient.post() unwraps the response, so response IS the BillingPortalResponse
      if (response.portalUrl) {
        const portalUrl = response.portalUrl

        // Direct navigation to avoid popup blockers
        window.location.href = portalUrl
      } else {
        console.error('Invalid billing portal response:', response)
        alert('Unable to open billing portal. Please contact support.')
      }
    } catch (err) {
      console.error('Error opening billing portal:', err)
      alert('Failed to open billing portal. Please try again or contact support.')
    }
  }

  if (loading) {
    return (
      <Box sx={{ mb: 3 }}>
        <LinearProgress />
      </Box>
    )
  }

  if (error || !trialStatus) {
    return null // Don't show error banner, just fail silently
  }

  // Active paid subscription
  if (trialStatus.hasActiveSubscription && trialStatus.accountStatus === 'active') {
    return (
      <Alert
        severity="success"
        sx={{ mb: 3 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={handleBillingPortal}
            startIcon={<CreditCard />}
          >
            Manage Billing
          </Button>
        }
      >
        <AlertTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle />
            Active Subscription
          </Box>
        </AlertTitle>
        Your {trialStatus.billingPlan} plan is active.{' '}
        {formatCurrencySimple(trialStatus.monthlyAmount, trialStatus.currency)}/month
      </Alert>
    )
  }

  // Expired trial
  if (trialStatus.accountStatus === 'expired_trial') {
    return (
      <Alert
        severity="error"
        sx={{ mb: 3 }}
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={handleUpgrade}
            startIcon={<CreditCard />}
          >
            Upgrade Now
          </Button>
        }
      >
        <AlertTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning />
            Trial Expired
          </Box>
        </AlertTitle>
        Your trial ended on {new Date(trialStatus.trialEndsAt).toLocaleDateString()}.
        Upgrade to continue using the service.
      </Alert>
    )
  }

  // Active trial
  if (trialStatus.isTrialActive) {
    const severity = trialStatus.daysRemaining <= 3 ? 'warning' : 'info'
    const progressValue = ((14 - trialStatus.daysRemaining) / 14) * 100

    return (
      <Alert
        severity={severity}
        sx={{ mb: 3 }}
        action={
          <Button
            color="inherit"
            size="small"
            onClick={handleUpgrade}
            startIcon={<CreditCard />}
          >
            Upgrade
          </Button>
        }
      >
        <AlertTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTime />
            Free Trial
            <Chip
              label={`${trialStatus.daysRemaining} days left`}
              size="small"
              color={trialStatus.daysRemaining <= 3 ? 'warning' : 'primary'}
            />
          </Box>
        </AlertTitle>

        <Box sx={{ mt: 1, mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Trial progress: Day {14 - trialStatus.daysRemaining} of 14
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Box>

        <Typography variant="body2">
          {trialStatus.daysRemaining <= 3 ? (
            <>⚠️ Your trial ends on {new Date(trialStatus.trialEndsAt).toLocaleDateString()}.
            Upgrade now to avoid service interruption.</>
          ) : (
            <>Your trial ends on {new Date(trialStatus.trialEndsAt).toLocaleDateString()}.
            Enjoy full access to all features!</>
          )}
        </Typography>
      </Alert>
    )
  }

  return null
}

export default TrialStatusBanner